<template>
  <aside class="sidebar-container">
    <div class="sidebar-title">{{props.title}}浏览</div>
    <div class="sidebar-search">
      <el-input v-model="filterText" :placeholder="'搜索'+props.title+'...'" :prefix-icon="Search" size="small"
        clearable />
    </div>

    <div class="sidebar-scroll-area">
      <div class="category-group" v-for="item in filteredData" :key="item.key">
        <div class="category-parent" :class="{ 
            'is-single': !hasChildren(item), 
            'is-active': activeName === item.key,
            'is-expanded': openParents.includes(item.key)
          }" @click="handleParentClick(item)">
          <div class="parent-left">
            <el-icon v-if="hasChildren(item)" class="arrow">
              <ArrowRight />
            </el-icon>
            <span v-else class="dot-placeholder"></span>
            <span class="label">{{ item.label }}({{ item.key }})</span>
          </div>
          <span class="count-badge">{{ item.doc_count }}</span>
        </div>

        <transition name="expand">
          <div v-show="openParents.includes(item.key)" v-if="hasChildren(item)" class="category-children">
            <div v-for="child in item.children" :key="child.key" class="category-child"
              :class="{ 'is-active': activeName === child.key }" @click="handleChildClick(child)">
              <span class="label">{{ child.label }}({{ child.key }})</span>
              <span class="count-badge">{{ child.doc_count }}</span>
            </div>
          </div>
        </transition>
      </div>

      <el-empty v-if="filteredData.length === 0" :image-size="60" :description="'未找到'+props.title" />
    </div>
  </aside>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { ArrowRight, Search } from "@element-plus/icons-vue";

  const props = defineProps({
    title: { type: String, default: "分类" },
    data: { type: Array, default: () => [] },
    modelValue: { type: String, default: "" }
  });

  const emit = defineEmits(['update:modelValue', 'change']);

  const filterText = ref("");
  const openParents = ref([]);
  const activeName = ref(props.modelValue.key);

  const hasChildren = (item) => item.children && item.children.length > 0;

  const normalizedData = computed(() => {
    return props.data.map(item => {
      let children = item.children;
      if (children && !Array.isArray(children)) {
        children = [children];
      }
      return { ...item, children: children || [] };
    });
  });

  const expandAll = () => {
    openParents.value = normalizedData.value
      .filter(item => hasChildren(item)) // 现在可以安全访问 hasChildren 了
      .map(item => item.key);
  };

  watch(() => props.data, (newVal) => {
    if (newVal && newVal.length > 0) {
      expandAll();
    }
  }, { immediate: true, deep: true });

  // 监听外部传参变化
  watch(() => props.modelValue, (newVal) => {
    activeName.value = newVal;
  });



  // 分类搜索逻辑
  const filteredData = computed(() => {
    if (!filterText.value) return props.data;
    const search = filterText.value.toLowerCase();
    return props.data.filter(item => {
      const parentMatch = item.label.toLowerCase().includes(search);
      const childrenMatch = item.children?.some(child => child.label.toLowerCase().includes(search));
      return parentMatch || childrenMatch;
    });
  });

  const handleParentClick = (item) => {
    if (hasChildren(item)) {
      const idx = openParents.value.indexOf(item.key);
      idx > -1 ? openParents.value.splice(idx, 1) : openParents.value.push(item.key);
      select(item);
    } else {
      select(item);
    }
  };

  const handleChildClick = (child) => select(child);

  const select = (item) => {
    activeName.value = item.key;
    emit('update:modelValue', item.key);
    emit('change', item);
  };
</script>

<style scoped>
  .sidebar-container {
    width: 100%;
    /* height: 100%; */
    background: #fff;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #edf2f7;
  }

  .sidebar-title {
    padding: 8px 12px;
    font-size: 12px;
    color: #909399;
    font-weight: bold;
    text-transform: uppercase;
  }

  .sidebar-search {
    padding: 16px;
    border-bottom: 1px solid #f7fafc;
  }

  .sidebar-scroll-area {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
  }

  /* 父级样式 */
  .category-parent {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    margin-bottom: 2px;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    color: #4a5568;
  }

  .category-parent:hover {
    background: #f7fafc;
    transform: translateX(4px);
    color: #2a5298;
  }

  .parent-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .arrow {
    font-size: 12px;
    transition: transform 0.3s ease;
    color: #a0aec0;
  }

  .is-expanded .arrow {
    transform: rotate(90deg);
    color: #2a5298;
  }

  .dot-placeholder {
    width: 12px;
  }

  .label {
    font-size: 14px;
    font-weight: 500;
  }

  .count-badge {
    font-size: 11px;
    padding: 2px 6px;
    background: #edf2f7;
    color: #718096;
    border-radius: 10px;
    min-width: 20px;
    text-align: center;
  }

  .is-active {
    background: #ebf4ff !important;
    color: #2a5298 !important;
  }

  .is-active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 20%;
    bottom: 20%;
    width: 3px;
    background: #2a5298;
    border-radius: 0 4px 4px 0;
  }

  .category-children {
    padding-left: 20px;
    margin: 4px 0;
    overflow: hidden;
  }

  .category-child {
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
    margin: 1px 0;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    color: #718096;
    transition: all 0.2s;
    position: relative;
  }

  .category-child:hover {
    background: #f7fafc;
    color: #2a5298;
  }

  .category-child.is-active {
    background: #f0f7ff;
    color: #2a5298;
    font-weight: 600;
  }

  .expand-enter-active,
  .expand-leave-active {
    transition: all 0.3s ease-out;
    max-height: 500px;
  }

  .expand-enter-from,
  .expand-leave-to {
    max-height: 0;
    opacity: 0;
  }
</style>